import { defineStore } from "pinia";
import { questionnaireService } from "@/utils/questionnaireRequest";
import router from "../routes";
import moment from "moment";
import { useCommonStore } from "@/stores/CommonStore.ts";

export const useGutZoomerStore = defineStore("GutZoomerStore", {
  state: () => {
    return {
      birthdate: "",
      qnrIsCompleted: false,
      gutZoomerQnr: {
        basic_info: {
          gender: null,
          birthdate: null,
          ethnicity: null,
          specific_asian_ethnicity: null,
          other_ethnicity: null,
        },
        gi_symptoms: {
          abdominal_pain: null,
          bad_breath: null,
          bloating: null,
          diarrhea: null,
          constipation: null,
          nausea: null,
          vomiting: null,
          acid_reflux: null,
          indigestion: null,
          flatulence: null,
          greasy_stools: null,
          mucous_in_stool: null,
          change_in_stool: null,
          blood_in_stool: null,
          anal_fissures: null,
        },
        systemic_symptoms: {
          frequent_infections: null,
          altered_weight: null,
          fatigue: null,
          over_eating: null,
          loss_of_appetite: null,
          joint_pain: null,
          skin_concerns: null,
          brain_fog: null,
          sleep_disturbances: null,
          fever: null,
        },
        dietary_patterns: {
          high_fat_foods: null,
          dairy_products: null,
          fermented_foods: null,
          fiber_rich_foods: null,
          processed_foods: null,
          sugary_foods: null,
          gluten_containing_foods: null,
          artificial_sweeteners: null,
        },
        digestive_function: {
          bowel_movement_frequency: null,
          stool_consistency: null,
        },
        health_concerns: {
          personal_history: {
            personal_ibs: null,
            personal_ibd: null,
            personal_celiac_disease: null,
            personal_sibo: null,
            personal_food_allergies: null,
            other_personal_conditions: null,
          },
          family_history: {
            family_ibs: null,
            family_ibd: null,
            family_celiac_disease: null,
            family_sibo: null,
            family_food_allergies: null,
            other_family_conditions: null,
          },
        },
        medication_history: {
          antibiotics: null,
          antibiotics_history: null,
          current_medications: null,
        },
        lifestyle: {
          active_level: null,
          sleep_hours: null,
          nicotine_use: null,
          alcohol_use: null,
          stress_level: null,
          stress_reduction: null,
        }
      },
      personalHistoryArray: [],
      familyHistoryArray: [],
      loadingQuestionnaire: false,
      loadingSubmit: false,
      patientHasDOB: false,
      patientHasGender: false,
    };
  },
  getters: {
    accession_id() {
      const commonStore = useCommonStore();
      return commonStore.accession_id;
    }
  },
  actions: {
    setGutZoomerQnr(val) {
      if (!val || typeof val !== "object") return;
      // Filter out any keys that are not in the template
      const mergeValues = (source, template) => {
        let result = { ...template };
        
        for (const key in template) {
          if (Object.prototype.hasOwnProperty.call(source, key)) {
            if (typeof template[key] === "object" && template[key] !== null) {
              result[key] = mergeValues(source[key], template[key]); 
            } else {
              result[key] = source[key]; 
            }
          }
        }
        return result;
      };
      
      this.gutZoomerQnr = mergeValues(val, this.gutZoomerQnr);
      this.convertGutZoomerQnrObjectToArray();
    },
    async getGutZoomerQnrData() {
      if (!this.accession_id) {
        router.push(`/auth/GutZoomer`);
        return;
      }
      const params = {
        accession_id: String(this.accession_id),
      };
      this.loadingQuestionnaire = true;
      try {
        const res = await questionnaireService.get(
          "/orders/samples/gut-zoomer/questionnaires",
          {
            params: params,
          }
        );
        if (res.data?.basic_info?.gender) {
          this.setGender(res.data?.basic_info?.gender);
          this.patientHasGender = true;
        }
        if (res.data?.basic_info.birthdate) {
          this.dateConversion(
            "birthdate",
            res.data?.basic_info?.birthdate
          );
          this.patientHasDOB = true;
        }
        if (res.data.completed_at) {
          this.setQnrIsCompleted(true);
        }
        this.setGutZoomerQnr(res.data);
        this.loadingQuestionnaire = false;
        return Promise.resolve(res.data);
      } catch (err) {
        console.log("failed to retrieve questionnaire", err);
        router.push(`/auth/GutZoomer/${this.accession_id}`);
        this.loadingQuestionnaire = false;
        return Promise.reject(err);
      }
    },
    submitGutZoomerQnr(val) {
      this.loadingSubmit = true;
      let params = this.gutZoomerQnr;
      params.accession_id = this.accession_id;
      params.save_for_now = val;
      console.log("submitGutZoomerQnr", params);
      this.loadingSubmit = false;
      questionnaireService
        .post("/orders/samples/gut-zoomer/questionnaires", params)
        .then(() => {
          this.loadingSubmit = false;
          if (!val) {
            router.push("/questionnaire_completed");
          }
        })
        .catch((err) => {
          this.loadingSubmit = false;
          router.push("/gutZoomerFailed");
          console.log("submitQuestionnaire", err);
        });
    },
    // Set functions
    setGender(val) {
      this.gutZoomerQnr.basic_info.gender = val;
    },
    setQnrIsCompleted(val) {
      const role = sessionStorage.getItem("role");
      this.qnrIsCompleted = role && role === "internal" ? false : val;
    },
    setGutZoomerQnrSingleField(key, val) {
      // Split the provided key into an array by the dot (.) separator to handle nested keys
      const keys = key.split(".");
      // Start from the root object (hormoneZoomerQnr)
      let currentObj = this.gutZoomerQnr;
      // Iterate through all keys except the last one, to navigate through the nested objects
      for (let i = 0; i < keys.length - 1; i++) {
        // Move deeper into the object at each level
        if (!currentObj[keys[i]]) {
          // If the key doesn't exist at the current level, initialize it as an empty object
          currentObj[keys[i]] = {};
        }
        currentObj = currentObj[keys[i]];
      }
      // Update the value at the final key
      currentObj[keys[keys.length - 1]] = val;
    },
    setPersonalHistoryArray(val) {
      this.personalHistoryArray = val;
    },
    setFamilyHistoryArray(val) {
      this.familyHistoryArray = val;
    },
    convertGutZoomerQnrObjectToArray() {
      this.personalHistoryArray = [];
      this.familyHistoryArray = [];
      // Iterate through the medical history object and push the keys that have a truthy value to the corresponding array
      for (const key in this.gutZoomerQnr.health_concerns) {
        for (const field in this.gutZoomerQnr.health_concerns[key]) {
          if (this.gutZoomerQnr.health_concerns[key][field]) {
            if (field === "other_personal_conditions") {
              this.personalHistoryArray.push("other");
            } else if (field === "other_family_conditions") {
              this.familyHistoryArray.push("other");
            } else {
              if (key === "personal_history") {
                this.personalHistoryArray.push(field);
              } else {
                this.familyHistoryArray.push(field);
              }
            }
          }
        }
      }
    },
    convertGutZoomerQnrArrayToObject(key, arr) {
      if (!(key in this.gutZoomerQnr.health_concerns)) {
        console.log("key not found in health_concerns");
        return;
      }
      // Iterate through the medical history object and set the value of each field to true if it's in the provided array
      for (const item in this.gutZoomerQnr.health_concerns[key]) {
        if (!item.includes("other")) {
          this.gutZoomerQnr.health_concerns[key][item] = arr.includes(item)
            ? true
            : false;
        }
      }
      // check the array, if user doesn't select 'other' then clear the other field
      if (!arr.includes("other")) {
        let other_key_name =
          key === "personal_history"
            ? "other_personal_conditions" : "other_family_conditions";
        this.gutZoomerQnr.health_concerns[key][other_key_name] = "";
      }
    },
    dateConversion(dataType, date) {
      if (!date || !moment(date).isValid()) {
        return;
      }
      if (dataType === "birthdate") {
        this.setBirthdate(moment(date).format("MM/DD/YYYY"));
      }
    },
    setBirthdate(val) {
      this.birthdate = val;
      this.gutZoomerQnr.basic_info.birthdate = moment(val, "MM/DD/YYYY").format(
        "YYYY-MM-DD"
      );
    },
  },
});
